import { useEffect, useMemo } from 'react';
import { Scenario } from 'api';
import { CapTableVersionWithCompanyId, ChildCompany } from 'common/types/company';
import useFundInvestmentSheetConfig from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import useWaterfallSheetConfig from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { RelatedEntitySheet } from 'pages/Waterfall/utilities/useRelatedEntitiesWaterfalls';
import { WeightedShareValue } from '../FundFirmDistribution/FundFirmDistribution.props';
import WaterfallTables from '../WaterfallTables/WaterfallTables';

const RelatedEntityWaterfallSheets = ({
  relatedEntity,
  scenarioResult,
  childrenCapTables,
  ownedSharesValue,
  registerChildrenSheets,
}: {
  relatedEntity: ChildCompany;
  scenarioResult: Scenario;
  childrenCapTables: CapTableVersionWithCompanyId[];
  ownedSharesValue: WeightedShareValue[];
  registerChildrenSheets: (sheets: RelatedEntitySheet[], idKey: number) => void;
}) => {
  const validSecurities =
    childrenCapTables.find(ct => Number(ct.company_id) === Number(relatedEntity.company_id))?.securities ?? [];
  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarioResult,
    validSecurities,
    companyName: relatedEntity.company_name,
    relatedEntityId: Number(relatedEntity.company_id),
  });
  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: ownedSharesValue,
    validSecurities,
    idToAssociateTo: Number(relatedEntity.company_id),
  });

  const sheets = useMemo(
    () => [waterfallSheet, ...fundInvestmentSheets].filter(sheet => !!sheet),
    [waterfallSheet, fundInvestmentSheets]
  );

  useEffect(() => {
    if (sheets.length) {
      registerChildrenSheets(sheets, Number(relatedEntity.company_id));
    }
  }, [sheets, registerChildrenSheets, relatedEntity.company_id]);

  if (!waterfallSheet) {
    return null;
  }

  return (
    <WaterfallTables
      waterfallSheet={waterfallSheet}
      fundInvestmentsSheets={fundInvestmentSheets}
    />
  );
};

export default RelatedEntityWaterfallSheets;
